import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Alert, Spinner, Badge, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Article Management Component
 * View and delete generated articles
 *
 * IMPORTANT: Modal pattern - see /.claude/modal-pattern.md for documentation.
 * Do not extract Modal into a separate component - it breaks CSS animations.
 */
const ArticleManagement = () => {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('production');
  const [costDetail, setCostDetail] = useState(null);
  const [loadingCosts, setLoadingCosts] = useState(false);
  const [keepingId, setKeepingId] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, [page, viewMode]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const dryRun = viewMode === 'test';
      const response = await fetch(`/api/admin/tools/articles?page=${page}&limit=20&dryRun=${dryRun}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setArticles(data.articles);
      setPagination(data.pagination);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch articles');
      setLoading(false);
    }
  };

  const fetchCostDetail = async (articleId) => {
    setLoadingCosts(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/tools/articles/${articleId}/costs`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch cost details');
      }
      const data = await response.json();
      setCostDetail(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch cost details');
      setCostDetail(null);
    } finally {
      setLoadingCosts(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    setError('');

    try {
      const response = await fetch(`/api/admin/tools/articles/${deleteTarget.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setDeleteTarget(null);
        fetchArticles();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete article');
      }
    } catch (err) {
      setError('Failed to delete article');
    } finally {
      setDeleting(false);
    }
  };

  const handleKeep = async (articleId) => {
    setKeepingId(articleId);
    setError('');

    try {
      const response = await fetch(`/api/admin/tools/articles/${articleId}/keep`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        fetchArticles();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to mark article as reviewed');
      }
    } catch (err) {
      setError('Failed to mark article as reviewed');
    } finally {
      setKeepingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const truncate = (str, len = 40) => {
    if (!str) return '-';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  const statusBadge = (status) => {
    const colors = {
      completed: 'success',
      partial: 'warning',
      failed: 'danger',
      processing: 'info',
      pending: 'secondary'
    };
    return <Badge bg={colors[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading && articles.length === 0) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  return (
    <AdminToolsLayout>
      {/* View Mode Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link
            active={viewMode === 'production'}
            onClick={() => { setViewMode('production'); setPage(1); }}
            style={{ cursor: 'pointer' }}
          >
            Production
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={viewMode === 'test'}
            onClick={() => { setViewMode('test'); setPage(1); }}
            style={{ cursor: 'pointer' }}
          >
            Test/Mock
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {viewMode === 'test' && (
        <Alert variant="info" className="mb-4">
          Showing articles from dry run/test mode.
        </Alert>
      )}

      <Card>
        <Card.Header>
          <h5 className="mb-0">Generated Articles</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Table responsive hover size="sm">
            <thead>
              <tr>
                <th>Title</th>
                <th>Source</th>
                <th>Status</th>
                <th>Cost</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article.id}>
                  <td>
                    <Link
                      to={`/tools/article-to-media/${article.slug}`}
                      target="_blank"
                      style={{ textDecoration: 'none' }}
                    >
                      {truncate(article.article_title, 30)}
                    </Link>
                  </td>
                  <td>{article.source_domain || '-'}</td>
                  <td>
                    {statusBadge(article.status)}
                    {article.is_reviewed && (
                      <Badge bg="info" className="ms-1" title="Admin reviewed - users cannot request deletion">
                        Reviewed
                      </Badge>
                    )}
                  </td>
                  <td>${parseFloat(article.total_cost || 0).toFixed(4)}</td>
                  <td style={{ fontSize: '0.875rem' }}>{formatDate(article.created_at)}</td>
                  <td>
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="me-1"
                      onClick={() => fetchCostDetail(article.id)}
                    >
                      Costs
                    </Button>
                    {!article.is_reviewed && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="me-1"
                        onClick={() => handleKeep(article.id)}
                        disabled={keepingId === article.id}
                      >
                        {keepingId === article.id ? '...' : 'Keep'}
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setDeleteTarget(article)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {pagination && pagination.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </small>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={page === pagination.totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal - DO NOT extract into separate component, see /.claude/modal-pattern.md */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Article</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this article?</p>
          {deleteTarget && (
            <p><strong>{deleteTarget.article_title}</strong></p>
          )}
          <Alert variant="warning">
            This will permanently delete the article and all associated files (audio, images) from S3.
            This action cannot be undone.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Article'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cost Detail Modal - DO NOT extract into separate component, see /.claude/modal-pattern.md */}
      <Modal show={!!costDetail} onHide={() => setCostDetail(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Cost Breakdown</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingCosts ? (
            <div className="text-center py-3"><Spinner animation="border" /></div>
          ) : costDetail && (
            <>
              <p className="mb-3">
                <strong>{costDetail.article?.article_title}</strong>
                <br />
                <small className="text-muted">Status: {costDetail.article?.status}</small>
              </p>

              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Model</th>
                    <th className="text-end">Calls</th>
                    <th className="text-end">Input Tokens</th>
                    <th className="text-end">Output Tokens</th>
                    <th className="text-end">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {costDetail.costBreakdown?.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <Badge bg={item.type === 'text' ? 'primary' : item.type === 'audio' ? 'warning' : item.type === 'image' ? 'success' : 'secondary'}>
                          {item.type}
                        </Badge>
                      </td>
                      <td><code>{item.model_used}</code></td>
                      <td className="text-end">{item.calls}</td>
                      <td className="text-end">{item.input_tokens || '-'}</td>
                      <td className="text-end">{item.output_tokens || '-'}</td>
                      <td className="text-end">${parseFloat(item.cost || 0).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-dark">
                    <td colSpan={5}><strong>Total</strong></td>
                    <td className="text-end"><strong>${parseFloat(costDetail.totalFromLog || 0).toFixed(4)}</strong></td>
                  </tr>
                </tfoot>
              </Table>

              {costDetail.article?.total_cost !== costDetail.totalFromLog && (
                <Alert variant="info" className="mt-2 mb-0">
                  <small>
                    Article record shows ${parseFloat(costDetail.article?.total_cost || 0).toFixed(4)} total.
                    Difference may be due to costs incurred before completion.
                  </small>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCostDetail(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminToolsLayout>
  );
};

export default ArticleManagement;
